/**
 * MentionToast — toast em tempo real para notificações do tipo "mention".
 *
 * Estratégia de detecção:
 *  - Na montagem, registra todos os IDs de mention já existentes como "vistos"
 *    (evita flood ao recarregar a página).
 *  - A cada poll do useNotifications, compara com o conjunto de IDs vistos.
 *  - Notificações mention não vistas → exibe toast, adiciona ao conjunto.
 */

import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AtSign, X } from "lucide-react";
import { useNotifications } from "@/hooks/useApi";

// ── Types ─────────────────────────────────────────────────────────────────────

interface ToastItem {
  id: string;
  title: string;
  body: string;
  link: string | null;
  exiting: boolean;
}

const TOAST_DURATION_MS = 7_000;
const TOAST_EXIT_MS     = 320;

// ── Provider ──────────────────────────────────────────────────────────────────

export function MentionToastProvider() {
  const { data } = useNotifications();
  const navigate = useNavigate();

  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const seenIds    = useRef<Set<string>>(new Set());
  const timers     = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const initialised = useRef(false);

  // Dismiss helper — marca exiting → remove após animação
  const dismiss = useCallback((id: string) => {
    setToasts((prev) =>
      prev.map((t) => (t.id === id ? { ...t, exiting: true } : t))
    );
    const t = setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
      timers.current.delete(id);
    }, TOAST_EXIT_MS);
    timers.current.set(id, t);
  }, []);

  useEffect(() => {
    return () => {
      // Cleanup timers on unmount
      timers.current.forEach(clearTimeout);
    };
  }, []);

  useEffect(() => {
    if (!data?.notifications) return;

    const mentions = data.notifications.filter((n) => n.type === "mention");

    // Primeira execução — marcar todos como vistos sem mostrar toast
    if (!initialised.current) {
      mentions.forEach((n) => seenIds.current.add(n.id));
      initialised.current = true;
      return;
    }

    // Execuções seguintes — somente mentions novos (não lidos + não vistos)
    const fresh = mentions.filter(
      (n) => !n.is_read && !seenIds.current.has(n.id)
    );

    fresh.forEach((n) => {
      seenIds.current.add(n.id);

      const toast: ToastItem = {
        id:      n.id,
        title:   n.title,
        body:    n.body ?? "",
        link:    n.link ?? "/operations",
        exiting: false,
      };

      setToasts((prev) => [...prev, toast]);

      // Auto-dismiss
      const timer = setTimeout(() => dismiss(n.id), TOAST_DURATION_MS);
      timers.current.set(n.id, timer);
    });
  }, [data, dismiss]);

  if (toasts.length === 0) return null;

  return (
    <div
      className="fixed bottom-6 right-6 z-[9999] flex flex-col gap-3 items-end pointer-events-none"
      aria-live="polite"
      aria-label="Notificações"
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          style={{
            animation: toast.exiting
              ? `mentionToastOut ${TOAST_EXIT_MS}ms ease-in forwards`
              : "mentionToastIn 280ms ease-out",
          }}
          className="pointer-events-auto relative w-[340px] bg-gl-black text-white shadow-2xl flex items-start gap-3 p-4 overflow-hidden"
        >
          {/* Ícone */}
          <div className="mt-0.5 flex-shrink-0 w-7 h-7 rounded-full bg-[#d9ed09] flex items-center justify-center">
            <AtSign className="w-3.5 h-3.5 text-black" />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-[0.12em] opacity-70 mb-0.5">
              {toast.title}
            </p>
            <p className="text-[13px] leading-snug">
              {toast.body}
            </p>
            {toast.link && (
              <button
                type="button"
                onClick={() => {
                  dismiss(toast.id);
                  navigate(toast.link!);
                }}
                className="mt-2 text-[11px] font-bold uppercase tracking-[0.1em] text-[#d9ed09] hover:opacity-80 transition-opacity cursor-pointer"
              >
                Ver atividade →
              </button>
            )}
          </div>

          {/* Dismiss */}
          <button
            type="button"
            onClick={() => dismiss(toast.id)}
            className="flex-shrink-0 opacity-50 hover:opacity-100 transition-opacity cursor-pointer mt-0.5"
            aria-label="Fechar"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Progress bar */}
          <div
            className="absolute bottom-0 left-0 h-[2px] bg-[#d9ed09]"
            style={{ animation: `mentionProgress ${TOAST_DURATION_MS}ms linear forwards` }}
          />
        </div>
      ))}

      {/* Keyframes injetados inline — sem dependência de CSS externo */}
      <style>{`
        @keyframes mentionToastIn {
          from { opacity: 0; transform: translateY(12px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)   scale(1); }
        }
        @keyframes mentionToastOut {
          from { opacity: 1; transform: translateX(0); }
          to   { opacity: 0; transform: translateX(24px); }
        }
        @keyframes mentionProgress {
          from { width: 100%; }
          to   { width: 0%; }
        }
      `}</style>
    </div>
  );
}
